import React, { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { AuthStatus } from "../../reducers/auth/authReducer";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import useAuthContext from "../../hooks/useAuthContext";
import ScheduledReportsService from "../../services/ScheduledReportsService";
import apiClient from "../../auth/apiClient";
import useHasPermissions from "../../hooks/useHasPermissions";
import { useStyles } from "./styles";
import clsx from "clsx";
import { FindEntity } from "../../state/slices/entities";
import { selectParentEntity, changeNewFacilities } from "../../state/slices/CoreEntity";
import { useCoreEntityContext } from "../../hooks/useCoreEntitySlice";
import { ENTITY_TYPE } from "../../constants";
import EntityService from "../../services/EntityService";
import _ from "lodash";
import { setLocalStorage, removeLocalStorage } from "../../utils/localStorage/LocalStorageService";
import useCurrentUser from "../../hooks/useCurrentUser";
import { DEFAULT_PROPERTY_ID, DEFAULT_SCOPE_AWARE_IDS } from "../../constants";
import { changeFacilities } from "../../state/slices/entities";
import { useHistory } from "react-router-dom";

export default function GetReportRedirect() {
  const classes = useStyles();
  const { hasPermissions } = useHasPermissions();
  const { facilityID } = useCurrentFacility();
  const isAdmin = useSelector((state) => state?.user?.isAdmin);
  const { rrid } = useParams();
  const { authReducer } = useAuthContext();
  const [authState] = authReducer;
  const scheduledReportsService = new ScheduledReportsService(apiClient);
  const entityService = new EntityService(apiClient);
  const [errorMessage, setErrorMessage] = useState(null);
  const useCoreEntitySlice = useCoreEntityContext();
  const currentUser = useCurrentUser();
  const history = useHistory();
  const dispatch = useDispatch();
  const parentEntity = useSelector((state) => {
      if (useCoreEntitySlice) return selectParentEntity(facilityID)(state);

      const entity = FindEntity(state.entities?.EntityList ?? [], facilityID);
      return FindEntity(state.entities?.EntityList ?? [], entity.parententityid);
  }, shallowEqual);
  const facilityGroupID =
    parentEntity?.typeid === ENTITY_TYPE.FacilityGroup ? parentEntity?.entityid : null;

  useEffect(() => {
    setLocalStorage(DEFAULT_PROPERTY_ID, facilityID);
  }, [facilityID]);

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const response = await scheduledReportsService.getReportJobbyRrid(rrid);
        if (!response.data) {
          console.error("ReportJob not found", response);
          setErrorMessage("Report not available.");
          return;
        }

        if (!isAdmin) {
          const jobData = JSON.parse(response?.data?.reportparams);
          const { facilitygroupid, facilityid } = jobData.parameters;

          if ((facilitygroupid != null && facilitygroupid !== facilityGroupID) ||
              (facilitygroupid == null && facilityid !== facilityID)) {
            let properties = await getProperties();
            if (properties.length === 0) {
              console.error("Failed to get properties", response);
              setErrorMessage("Failed to download the report.");
              return;
            }

            const filtered = collectEntitiesByType(properties, ENTITY_TYPE.Facility);
            const filteredFacilityGroups = collectEntitiesByType(properties, ENTITY_TYPE.FacilityGroup);

            const foundEntity = _.find(filtered, (entity) => entity.id === facilityid);
            const foundFacilityGroup = _.find(filteredFacilityGroups, (entity) => entity.id === facilitygroupid);

            if (!foundEntity && !foundFacilityGroup) {
              console.error("User does not have permission for this facility", response);
              setErrorMessage("You do not have permission to download this report.");
              return;
            }
            handleSelectedProperty(foundEntity.id);
            return;
          }

          if (!hasPermissions([jobData.reportname])) {
            console.error("User does not have permission", response);
            setErrorMessage("You do not have permission to download this report.");
            return;
          }
        }

        const urlResponse = await scheduledReportsService.getSignedDownloadURL(rrid);
        if (!urlResponse.data) {
          console.error("Signed download URL could not be fetched", urlResponse);
          setErrorMessage("Report not available.");
          return;
        }

        window.location.replace(urlResponse.data);
      } catch (err) {
        if (err.response?.status === 404) {
          console.error("Report not found", err);
          setErrorMessage("Report not available. It may have expired.");
        } else {
          console.error("Failed to get signed download URL", err);
          setErrorMessage("Failed to download the report.");
        }
      }
    };

    if (authState.authStatus === AuthStatus.AUTHENTICATED) {
      fetchUrl();
    }
  }, [
    authState.authStatus,
    rrid,
    facilityID,
    facilityGroupID
  ]);

  const getProperties = async () => {
    let properties = [];
    try {
      const result = await entityService.getPropertiesByScopes();
      properties = result.data;
      if (result?.status !== 200) {
        console.error("Failed to get properties", result);
      }
    } catch (err) {
      console.error(err);
    }

    return properties;
  };

  const collectEntitiesByType = (entityList, targetType) => {
    const results = [];

    function walk(node) {
      if (!node) return;

      if (node.entityType === targetType) {
        results.push(node);
      }

      if (Array.isArray(node.children)) {
        for (const child of node.children) {
          walk(child);
        }
      }
    }

    if (Array.isArray(entityList)) {
      for (const item of entityList) {
        walk(item);
      }
    } else {
      walk(entityList);
    }

    return results;
  }

  const handleSelectedProperty = async facilityID => {
    if (!facilityID) return;
    removeLocalStorage(DEFAULT_SCOPE_AWARE_IDS);
    history.push(`/getreport/${rrid}`);
    await dispatch(
      useCoreEntitySlice ?
        changeNewFacilities({ facilityID, userID: currentUser.UserID })
        :
        changeFacilities({ facilityID, userID: currentUser.UserID })
    );
  };

  if (authState.authStatus !== AuthStatus.AUTHENTICATED) {
    return (
      <Redirect
        to={{
          pathname: "/login",
          state: { redirectTo: `/getreport/${rrid}` },
        }}
      />
    );
  }

  if (errorMessage) {
    return (
      <div className={clsx([classes.errormessage])} role="alert">
        {errorMessage}
      </div>
    );
  }

  return null;
}
